import { ReactNode, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { createPortal } from "react-dom";
import { CustomButton } from "@/components/ui/Buttons";
import {
  getGoogleDriveStoreUrl,
  isMobilePhone,
  useIsMobilePhone,
} from "@/utils/isMobilePhone";

export type DriveFolderLinkButtonProps = {
  folderUrl: string;
  className?: string;
  variant?: "upload" | "view";
  children?: ReactNode;
  /** Scroll / focus direct upload when user picks "Use another upload method". */
  onPreferDirectUpload?: () => void;
};

type SheetStep = "main" | "afterInstall" | "openFailed";

function DriveMobileBottomSheet({
  open,
  onClose,
  folderUrl,
  variant,
  onPreferDirectUpload,
}: {
  open: boolean;
  onClose: () => void;
  folderUrl: string;
  variant: "upload" | "view";
  onPreferDirectUpload?: () => void;
}) {
  const [step, setStep] = useState<SheetStep>("main");

  useEffect(() => {
    if (open) setStep("main");
  }, [open, folderUrl]);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const storeUrl = getGoogleDriveStoreUrl();

  const tryOpenFolderAndClose = () => {
    const w = window.open(folderUrl, "_blank", "noopener,noreferrer");
    if (!w) {
      setStep("openFailed");
      return false;
    }
    onClose();
    return true;
  };

  const handleInstallDrive = () => {
    window.open(storeUrl, "_blank", "noopener,noreferrer");
    setStep("afterInstall");
  };

  const handleUseAnotherMethod = () => {
    onClose();
    onPreferDirectUpload?.();
  };

  const isUpload = variant === "upload";

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[999] flex flex-col justify-end"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <button
            type="button"
            aria-label="Dismiss"
            className="absolute inset-0 bg-black/40"
            onClick={onClose}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="drive-sheet-title"
            className="relative z-10 w-full max-h-[min(88vh,640px)] overflow-y-auto rounded-t-2xl bg-white shadow-xl"
            style={{
              paddingBottom: "max(1.25rem, env(safe-area-inset-bottom, 0px))",
            }}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 320 }}
          >
            <div className="mx-auto w-full max-w-md px-5 pt-3 pb-2">
              <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-[#E4E7EC]" />
            </div>

            <div className="mx-auto max-w-md px-5 pb-6 space-y-5">
              {step === "openFailed" && (
                <>
                  <h2
                    id="drive-sheet-title"
                    className="text-lg font-semibold text-[#101828] tracking-tight"
                  >
                    Couldn&apos;t open Google Drive
                  </h2>
                  <p className="text-sm text-[#475467] leading-relaxed">
                    {isUpload
                      ? "Try again, or use another way to finish your upload."
                      : "Try again, or open the link in your browser."}
                  </p>
                  <CustomButton
                    type="button"
                    onClick={() => tryOpenFolderAndClose()}
                    className="w-full"
                    backgroundColor="#17457D"
                  >
                    Try again
                  </CustomButton>
                  <CustomButton
                    type="button"
                    variant="outline"
                    onClick={() => {
                      window.open(folderUrl, "_blank", "noopener,noreferrer");
                      onClose();
                    }}
                    className="w-full"
                    backgroundColor="#17457D"
                    color="#17457D"
                  >
                    Open in browser
                  </CustomButton>
                  {isUpload ? (
                    <button
                      type="button"
                      onClick={handleUseAnotherMethod}
                      className="w-full text-center text-sm font-medium text-[#475467] py-2"
                    >
                      Use another upload method
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={onClose}
                      className="w-full text-center text-sm font-medium text-[#475467] py-2"
                    >
                      Close
                    </button>
                  )}
                </>
              )}

              {step === "afterInstall" && (
                <>
                  <h2
                    id="drive-sheet-title"
                    className="text-lg font-semibold text-[#101828] tracking-tight"
                  >
                    {isUpload ? "Google Drive is ready" : "Continue in Google Drive"}
                  </h2>
                  <p className="text-sm text-[#475467] leading-relaxed">
                    {isUpload
                      ? "You can continue uploading your file now."
                      : "Open the folder in the app when you’re ready."}
                  </p>
                  <p className="text-xs text-[#667085] leading-snug">
                    You’ll return here after selecting your file.
                  </p>
                  <CustomButton
                    type="button"
                    onClick={() => tryOpenFolderAndClose()}
                    className="w-full"
                    backgroundColor="#17457D"
                  >
                    {isUpload ? "Continue upload" : "Open Google Drive"}
                  </CustomButton>
                  {isUpload && (
                    <button
                      type="button"
                      onClick={handleUseAnotherMethod}
                      className="w-full text-center text-sm font-medium text-[#475467] py-2"
                    >
                      Use another upload method
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setStep("main")}
                    className="w-full text-center text-xs text-[#98A2B3] py-1"
                  >
                    Back
                  </button>
                </>
              )}

              {step === "main" && (
                <>
                  <h2
                    id="drive-sheet-title"
                    className="text-lg font-semibold text-[#101828] tracking-tight"
                  >
                    {isUpload
                      ? "Upload from Google Drive"
                      : "Open in Google Drive"}
                  </h2>
                  <p className="text-sm text-[#475467] leading-relaxed">
                    {isUpload
                      ? "To choose files from Google Drive on your phone, you’ll need the Google Drive app installed."
                      : "On your phone, this link works best in the Google Drive app."}
                  </p>
                  <p className="text-xs text-[#667085] leading-snug">
                    You’ll return here after selecting your file.
                  </p>
                  <CustomButton
                    type="button"
                    onClick={() => tryOpenFolderAndClose()}
                    className="w-full"
                    backgroundColor="#17457D"
                  >
                    Open Google Drive
                  </CustomButton>
                  <CustomButton
                    type="button"
                    variant="outline"
                    onClick={handleInstallDrive}
                    className="w-full"
                    backgroundColor="#17457D"
                    color="#17457D"
                  >
                    Install Google Drive
                  </CustomButton>
                  {isUpload ? (
                    <button
                      type="button"
                      onClick={handleUseAnotherMethod}
                      className="w-full text-center text-sm font-medium text-[#475467] py-2"
                    >
                      Use another upload method
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        window.open(folderUrl, "_blank", "noopener,noreferrer");
                        onClose();
                      }}
                      className="w-full text-center text-xs text-[#98A2B3] py-2"
                    >
                      Continue in browser
                    </button>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

export function DriveFolderLinkButton({
  folderUrl,
  className = "bg-blue-500 hover:text-white font-medium text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition",
  variant = "upload",
  children,
  onPreferDirectUpload,
}: DriveFolderLinkButtonProps) {
  const [sheetOpen, setSheetOpen] = useState(false);

  const openFolderDesktop = () => {
    window.open(folderUrl, "_blank", "noopener,noreferrer");
  };

  const handleTriggerClick = () => {
    if (isMobilePhone()) {
      setSheetOpen(true);
      return;
    }
    openFolderDesktop();
  };

  const label =
    children ??
    (variant === "view" ? "Open in Drive" : "Open Google Drive folder");

  return (
    <>
      <button type="button" onClick={handleTriggerClick} className={className}>
        {label}
      </button>

      <DriveMobileBottomSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        folderUrl={folderUrl}
        variant={variant}
        onPreferDirectUpload={onPreferDirectUpload}
      />
    </>
  );
}

export function DriveFolderMobileHint({
  variant = "upload",
}: {
  variant?: "upload" | "view";
}) {
  const mobile = useIsMobilePhone();
  if (!mobile) return null;
  return (
    <p className="text-[#B54708] text-xs text-center mt-2 max-w-md mx-auto leading-snug bg-amber-50/80 border border-amber-100/80 rounded-lg px-3 py-2">
      {variant === "view"
        ? "On your phone, we’ll open this in the Google Drive app when you continue."
        : "On your phone, finish this step in the Google Drive app—then come back here to submit. Or upload the file above instead."}
    </p>
  );
}
